import { useState, useEffect, useRef } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { 
  MessageCircle, X, Send, Bot, User, Minimize2, 
  Maximize2, Trash2, RefreshCw 
} from "lucide-react";
import { API } from "../App";
import axios from "axios";

export default function AIChatWidget() {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Check if user is authenticated
  const token = localStorage.getItem("token");
  const isAuthenticated = !!token;

  useEffect(() => {
    if (isOpen && messages.length === 0) {
      // Add welcome message
      setMessages([{
        role: "assistant",
        content: "Hi! I'm TheRentPayer AI Assistant. 👋 I can help you with:\n\n• Policy & coverage questions\n• Quote calculations\n• Claim filing process\n• Payment & billing inquiries\n• Withdrawal help\n\nHow can I assist you today?",
        timestamp: new Date().toISOString()
      }]);
    }
  }, [isOpen]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const sendMessage = async (e) => {
    e?.preventDefault();
    if (!input.trim() || loading) return;

    const userMessage = {
      role: "user",
      content: input.trim(),
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);
    setInput("");
    setLoading(true);

    try {
      const endpoint = isAuthenticated ? "/chat/message" : "/chat/public";
      const headers = isAuthenticated 
        ? { Authorization: `Bearer ${token}` }
        : {};

      const response = await axios.post(
        `${API}${endpoint}`,
        { message: userMessage.content, session_id: sessionId },
        { headers }
      );

      const assistantMessage = {
        role: "assistant",
        content: response.data.response,
        timestamp: new Date().toISOString()
      };

      setMessages(prev => [...prev, assistantMessage]);
      if (response.data.session_id) {
        setSessionId(response.data.session_id);
      }
    } catch (error) {
      const errorMessage = {
        role: "assistant",
        content: "I'm sorry, I'm having trouble responding right now. Please try again or contact support@therentpayer.com.",
        timestamp: new Date().toISOString(),
        isError: true
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setLoading(false);
      inputRef.current?.focus();
    }
  };

  const clearChat = () => {
    setMessages([{
      role: "assistant",
      content: "Chat cleared! How can I help you today?",
      timestamp: new Date().toISOString()
    }]);
    setSessionId(null);
  };

  const formatMessage = (content) => {
    // Simple markdown-like formatting
    return content.split('\n').map((line, i) => {
      if (line.startsWith('•')) {
        return <li key={i} className="ml-4">{line.substring(1).trim()}</li>;
      }
      return <p key={i} className={line ? "mb-1" : "mb-2"}>{line}</p>;
    });
  };

  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-20 right-6 bg-primary hover:bg-primary/90 text-white p-4 rounded-full shadow-lg transition-all hover:scale-110"
        style={{ zIndex: 9999 }}
        data-testid="chat-widget-button"
      >
        <MessageCircle className="h-6 w-6" />
        <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center animate-pulse">
          AI
        </span>
      </button>
    );
  }

  return (
    <div 
      className={`fixed bottom-20 right-6 transition-all duration-300 ${
        isMinimized ? 'w-72' : 'w-96'
      }`}
      style={{ zIndex: 9999 }}
      data-testid="chat-widget"
    >
      <div className="bg-white rounded-2xl shadow-2xl border border-border overflow-hidden">
        {/* Header */}
        <div className="bg-primary text-white p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="bg-white/20 p-2 rounded-lg">
              <Bot className="h-5 w-5" />
            </div>
            <div>
              <h3 className="font-semibold">TheRentPayer AI</h3>
              <p className="text-xs text-white/80">
                {isAuthenticated ? "Personalized Support" : "General Inquiries"}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={clearChat}
              className="p-2 hover:bg-white/20 rounded-lg transition-colors"
              title="Clear chat"
            >
              <Trash2 className="h-4 w-4" />
            </button>
            <button
              onClick={() => setIsMinimized(!isMinimized)}
              className="p-2 hover:bg-white/20 rounded-lg transition-colors"
            >
              {isMinimized ? <Maximize2 className="h-4 w-4" /> : <Minimize2 className="h-4 w-4" />}
            </button>
            <button
              onClick={() => setIsOpen(false)}
              className="p-2 hover:bg-white/20 rounded-lg transition-colors"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Messages */}
        {!isMinimized && (
          <>
            <div className="h-96 overflow-y-auto p-4 space-y-4 bg-gray-50">
              {messages.map((msg, idx) => (
                <div
                  key={idx}
                  className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[85%] rounded-2xl px-4 py-3 ${
                      msg.role === 'user'
                        ? 'bg-primary text-white rounded-br-md'
                        : msg.isError
                        ? 'bg-red-50 text-red-800 border border-red-200 rounded-bl-md'
                        : 'bg-white text-foreground shadow-sm border border-border rounded-bl-md'
                    }`}
                  >
                    <div className="flex items-start gap-2">
                      {msg.role === 'assistant' && (
                        <Bot className={`h-4 w-4 mt-1 flex-shrink-0 ${msg.isError ? 'text-red-500' : 'text-primary'}`} />
                      )}
                      <div className="text-sm leading-relaxed">
                        {formatMessage(msg.content)}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
              
              {loading && (
                <div className="flex justify-start">
                  <div className="bg-white rounded-2xl rounded-bl-md px-4 py-3 shadow-sm border border-border">
                    <div className="flex items-center gap-2">
                      <Bot className="h-4 w-4 text-primary" />
                      <div className="flex gap-1">
                        <span className="w-2 h-2 bg-primary/60 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
                        <span className="w-2 h-2 bg-primary/60 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
                        <span className="w-2 h-2 bg-primary/60 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              
              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <form onSubmit={sendMessage} className="p-4 border-t border-border bg-white">
              <div className="flex gap-2">
                <Input
                  ref={inputRef}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder="Type your message..."
                  disabled={loading}
                  className="flex-1 rounded-full"
                  data-testid="chat-input"
                />
                <Button
                  type="submit"
                  disabled={loading || !input.trim()}
                  className="rounded-full px-4"
                  data-testid="chat-send"
                >
                  <Send className="h-4 w-4" />
                </Button>
              </div>
              {!isAuthenticated && (
                <p className="text-xs text-muted-foreground text-center mt-2">
                  <a href="/login" className="text-primary hover:underline">Sign in</a> for personalized support
                </p>
              )}
            </form>
          </>
        )}
      </div>
    </div>
  );
}
